/**
 * BIRHAUS v3.0 Glass Morphism System
 * Modern translucent surfaces with backdrop blur effects
 */

export const glassV3 = {
  // Surface opacity levels - Light mode
  surfaces: {
    clear: 'rgba(255, 255, 255, 0.1)',
    light: 'rgba(255, 255, 255, 0.3)',
    medium: 'rgba(255, 255, 255, 0.5)',
    frosted: 'rgba(255, 255, 255, 0.7)',
    solid: 'rgba(255, 255, 255, 0.9)',
    opaque: 'rgba(255, 255, 255, 1)',
  },
  
  // Surface opacity levels - Dark mode
  surfacesDark: {
    clear: 'rgba(0, 0, 0, 0.1)',
    light: 'rgba(0, 0, 0, 0.3)',
    medium: 'rgba(0, 0, 0, 0.5)',
    frosted: 'rgba(0, 0, 0, 0.7)',
    solid: 'rgba(0, 0, 0, 0.9)',
    opaque: 'rgba(0, 0, 0, 1)',
  },
  
  // Backdrop blur intensities
  blur: {
    none: 'blur(0px)',
    xs: 'blur(2px)',
    sm: 'blur(4px)',
    md: 'blur(8px)',
    lg: 'blur(12px)',
    xl: 'blur(24px)',
    '2xl': 'blur(40px)',
    '3xl': 'blur(64px)',
  },
  
  // Saturate effects for enhanced glass feel
  saturate: {
    none: 'saturate(100%)',
    low: 'saturate(120%)',
    medium: 'saturate(150%)',
    high: 'saturate(180%)',
  },
  
  // Border colors for glass elements
  borders: {
    light: 'rgba(255, 255, 255, 0.2)',
    medium: 'rgba(255, 255, 255, 0.3)',
    strong: 'rgba(255, 255, 255, 0.4)',
    accent: 'rgba(255, 255, 255, 0.5)',
  },
  
  // Dark mode border colors
  bordersDark: {
    light: 'rgba(255, 255, 255, 0.1)',
    medium: 'rgba(255, 255, 255, 0.15)',
    strong: 'rgba(255, 255, 255, 0.2)',
    accent: 'rgba(255, 255, 255, 0.25)',
  },
  
  // Pre-composed glass effects (CSS-ready)
  effects: {
    // Subtle glass effect
    subtle: {
      background: 'rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(4px) saturate(120%)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.06)',
    },
    
    // Light glass effect
    light: {
      background: 'rgba(255, 255, 255, 0.3)',
      backdropFilter: 'blur(8px) saturate(150%)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.06)',
    },
    
    // Medium glass effect (most common)
    medium: {
      background: 'rgba(255, 255, 255, 0.5)',
      backdropFilter: 'blur(12px) saturate(150%)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      boxShadow: '0 16px 48px -12px rgba(0, 0, 0, 0.08)',
    },
    
    // Heavy glass effect
    heavy: {
      background: 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur(24px) saturate(180%)',
      border: '1px solid rgba(255, 255, 255, 0.4)',
      boxShadow: '0 24px 64px -16px rgba(0, 0, 0, 0.10)',
    },
    
    // Ultra glass effect
    ultra: {
      background: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(40px) saturate(180%)',
      border: '1px solid rgba(255, 255, 255, 0.5)',
      boxShadow: '0 32px 80px -20px rgba(0, 0, 0, 0.12)',
    },
  },
  
  // Dark mode glass effects
  effectsDark: {
    subtle: {
      background: 'rgba(0, 0, 0, 0.2)',
      backdropFilter: 'blur(4px) saturate(120%)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.3)',
    },
    
    light: {
      background: 'rgba(0, 0, 0, 0.3)',
      backdropFilter: 'blur(8px) saturate(150%)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.3)',
    },
    
    medium: {
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(12px) saturate(150%)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 16px 48px -12px rgba(0, 0, 0, 0.4)',
    },
    
    heavy: {
      background: 'rgba(0, 0, 0, 0.7)',
      backdropFilter: 'blur(24px) saturate(180%)',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      boxShadow: '0 24px 64px -16px rgba(0, 0, 0, 0.5)',
    },
    
    ultra: {
      background: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(40px) saturate(180%)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      boxShadow: '0 32px 80px -20px rgba(0, 0, 0, 0.6)',
    },
  },
} as const

// Component-specific glass presets
export const componentGlass = {
  button: glassV3.effects.light,
  card: glassV3.effects.medium,
  modal: glassV3.effects.heavy,
  navigation: glassV3.effects.medium,
  dropdown: glassV3.effects.heavy,
  tooltip: glassV3.effects.light,
  overlay: glassV3.effects.subtle,
  sidebar: glassV3.effects.medium,
  header: glassV3.effects.light,
  list: glassV3.effects.light,
  table: glassV3.effects.medium,
  tableHeader: glassV3.effects.heavy,
  toast: glassV3.effects.medium,
  mobileMenu: glassV3.effects.heavy,
} as const

// Tailwind-compatible class strings
export const glassClasses = {
  subtle: 'bg-white/20 backdrop-blur-sm saturate-[120%] border border-white/20 shadow-lg',
  light: 'bg-white/30 backdrop-blur-md saturate-[150%] border border-white/20 shadow-lg',
  medium: 'bg-white/50 backdrop-blur-lg saturate-[150%] border border-white/30 shadow-xl',
  heavy: 'bg-white/70 backdrop-blur-xl saturate-[180%] border border-white/40 shadow-2xl',
  ultra: 'bg-white/80 backdrop-blur-2xl saturate-[180%] border border-white/50 shadow-2xl',
  
  // Dark mode variants
  'dark-subtle': 'dark:bg-black/20 dark:border-white/10',
  'dark-light': 'dark:bg-black/30 dark:border-white/15',
  'dark-medium': 'dark:bg-black/50 dark:border-white/20',
  'dark-heavy': 'dark:bg-black/70 dark:border-white/25',
  'dark-ultra': 'dark:bg-black/80 dark:border-white/30',
} as const

// Animation-ready glass states
export const glassStates = {
  inactive: glassV3.effects.subtle,
  active: glassV3.effects.light,
  hovered: glassV3.effects.medium,
  focused: glassV3.effects.heavy,
  pressed: {
    ...glassV3.effects.light,
    transform: 'scale(0.98)',
  },
} as const